import React from 'react';
import Swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';
import {
  EnhancedAppointment,
  AppointmentStatus,
  Country,
  JsonObject,
} from '@bb/domain/dist/types';
import { ILocationService, IRootScopeService, route } from 'angular';
import FeedbackView from '@app/listing/appointments/feedback/FeedbackView';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import { message } from 'components';
import CovidSignRequired from '../../covid19/CovidSignRequired';
import COVID19AptAction from '../../covid19/COVID19AptAction';
import {
  NewAppointmentAlert,
  AppointmentDetailsSection,
  AppointmentInstructions,
  CalendarSyncButton,
} from '../../DashboardComponents/AppointmentDetailViewComponents';
import CalendarSyncProviderModal from '../../DashboardComponents/CalendarSyncProviderModal';
import ExternalStatusChange from '../ExternalStatusChange/ExternalStatusChange';
import getExternalFeedback, {
  ExternalFeedback,
} from '../../../api/AppExternalController/getExternalFeedback';
import { ContactType } from '../../../contexts/ExternalAuthContext';

type ExternalAuthType = {
  auth: {
    dashboardId?: string;
    retsId?: string;
    externalDashboard?: string;
    coopDashboard?: string;
    retsDashboard?: string;
    listing?: todo;
    ownerOrg?: any;
    user?: {
      id?: string;
      contactType: ContactType | 'coop';
    };
  };
};

interface EditAppointmentProps {
  $route: route.IRouteService;
  $routeParams: route.IRouteParamsService;
  $location: ILocationService;
  $rootScope: IRootScopeService;
  ExternalAuth: ExternalAuthType;
  ExternalService: any;
  AlmanacService: any;
  ToastService: any;
  appointment: JsonObject<EnhancedAppointment>;
  newStatus: AppointmentStatus;
  isNewAppointment?: boolean;
  clearSelected: () => void;
  serverDate: Date;
  updateAppointment: () => void;
  country: Country;
  showFeedback?: boolean;
}

interface EditAppointmentState {
  calendarModalOpen: boolean;
  errorMessage: string;
  almanacLoading: boolean;
  isAlmanacAuthenticated: boolean;
  existingAlmanacEvent: boolean;
  lockboxPhoto: string;
  sendingRequest: boolean;
  isSyncButtonClicked: boolean;
  showCalendarModal: boolean;
  redirectToAppointments: boolean;
}

const EditAppointment: React.FC<EditAppointmentProps> = ({
  $route,
  $routeParams,
  $location,
  $rootScope,
  ExternalAuth,
  ExternalAuth: {
    auth: {
      dashboardId,
      retsId,
      externalDashboard,
      coopDashboard,
      retsDashboard,
      user: { contactType },
      listing,
      ...auth
    },
  },
  ExternalService,
  AlmanacService,
  ToastService,

  appointment,
  newStatus,
  isNewAppointment,
  clearSelected,
  serverDate,
  updateAppointment,
  country,
  showFeedback,
}) => {
  const { lg } = useBreakpoint();
  const isCovidFlagEnabled = React.useMemo(() => contactType !== 'client'
      ? ExternalAuth.auth.listing.org.features.includes('COVID_19')
      : ExternalAuth.auth.ownerOrg.features.includes('COVID_19'), [contactType, ExternalAuth]);
  const [feedback, setFeedback] = React.useState<ExternalFeedback>(null);
  const [state, setState] = React.useState<EditAppointmentState>({
    calendarModalOpen: false,
    errorMessage: '',
    almanacLoading: true,
    isAlmanacAuthenticated: false,
    existingAlmanacEvent: false,
    lockboxPhoto: '',
    sendingRequest: false,
    isSyncButtonClicked: false,
    showCalendarModal: false,
    redirectToAppointments: false,
  });
  const cameFromSignEmail = $routeParams.sign === 'true';
  const backLinkRouteForExternalDashboard = appointment.buyers?.includes(
    ExternalAuth.auth.user?.id,
  )
    ? `/dash/${dashboardId}/buyer_contact_appointments`
    : `/dash/${dashboardId}/appointments/listing/${appointment.listing.id}`;

  const calendarSyncSuccess = () => {
    Swal.fire({
      title: 'Showing Synced!',
      text: 'The showing event should now appear in your calendar',
      type: 'success',
      confirmButtonText: 'Ok',
      showCancelButton: false,
    });
  };

  const coopAcceptTimeChange = () => {
    Swal.fire({
      title: 'Time Change Suggestion Accepted',
      text: 'Congrats! Your showing has been successfully modified.',
      type: 'success',
    })
      .then(() => {
        setState({ ...state, sendingRequest: true });
        ExternalService.saveDashboardStatus({
          status: 'confirm',
          dashboardId,
          appointmentId: appointment.id,
        }).then(() => {
          setState({
            ...state,
            redirectToAppointments: true,
            sendingRequest: false,
          });
        });
      })
      .catch(err => {
        errorReporter.error(err);
        setState({ ...state, sendingRequest: false });
      });
  };

  const coopDenyTimeChange = () => {
    Swal.fire({
      title: 'Deny Suggestion',
      text: 'Are you sure you want to Deny this time change suggestion?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db5e42',
      confirmButtonText: 'Yes',
    })
      .then(({ value }) => {
        if (value) {
          setState({ ...state, sendingRequest: true });
          ExternalService.saveDashboardStatus({
            status: 'cancel',
            dashboardId,
            appointmentId: appointment.id,
          }).then(() => {
            setState({
              ...state,
              redirectToAppointments: true,
              sendingRequest: false,
            });
          });
        }
      })
      .catch(err => {
        errorReporter.error(err);
        setState({ ...state, sendingRequest: false });
      });
  };

  const openCalendarModal = () => {
    setState({ ...state, showCalendarModal: true });
  };

  const redirectCalendar = async (service: string) => {
    const dashType = dashboardId
      ? `&dashboardId=${dashboardId}`
      : `&retsId=${retsId}`;
    const hasQueryParam = Object.keys($location.search()).length !== 0;
    const redirectUrl = `/almanac/connect?service=${service}${dashType}&redirectUrl=${encodeURIComponent(
      `${$location.absUrl()}${hasQueryParam ? '&' : '?'}action=sync`,
    )}`;
    window.location.href = redirectUrl;
  };

  const onCalendarSync = async () => {
    try {
      await AlmanacService.createEvent({ dashboardId, retsId }, appointment.id);
      return true;
    } catch (err) {
      errorReporter.error(err);
      Swal.fire({
        title: 'Error: Failed to sync showing',
        type: 'error',
      });
      return false;
    }
  };

  React.useEffect(() => {
    const init = async () => {
      const stateChanges: Partial<EditAppointmentState> = {
        errorMessage: '',
        lockboxPhoto: '',
        isAlmanacAuthenticated: false,
        existingAlmanacEvent: false,
        showCalendarModal: false,
      };

      if ($routeParams.sync === 'new') {
        calendarSyncSuccess();
      }
      if ($routeParams.error) {
        stateChanges.errorMessage = $routeParams.error;
        ToastService.add({
          message: `Could not sync calendar: ${stateChanges.errorMessage}`,
          type: 'danger',
        });
      }

      if (coopDashboard) {
        if (appointment.status === 'confirm') {
          const { lockboxPhoto } =
            await ExternalService.getLockboxPhoto(dashboardId);
          if (lockboxPhoto) stateChanges.lockboxPhoto = lockboxPhoto;
        }
        if (appointment.listingContactTimeChange) {
          if ($routeParams.status === 'autoconfirm') {
            coopAcceptTimeChange();
          } else if ($routeParams.status === 'deny') {
            coopDenyTimeChange();
          }
        }
      }

      try {
        const { isAuthenticated } = await AlmanacService.getAuthStatus({
          dashboardId,
          retsId,
        });
        const { eventId } = await AlmanacService.findAlmanacEvent(
          { dashboardId, retsId },
          appointment.id,
        );

        stateChanges.isAlmanacAuthenticated = isAuthenticated;
        stateChanges.existingAlmanacEvent = Boolean(eventId);

        if (!retsDashboard) {
          // Handle automatic sync action
          // Automatic sync on a rets dashboard is handled in the bbExternalBook component

          const { action } = $routeParams;
          if (
            isAuthenticated &&
            !eventId &&
            action === 'sync' &&
            !stateChanges.errorMessage
          )
            stateChanges.existingAlmanacEvent = await onCalendarSync();
        }

        if ($routeParams.sync === 'emaillink') {
          if (stateChanges.existingAlmanacEvent) {
            calendarSyncSuccess();
          } else if (isAuthenticated) {
            stateChanges.existingAlmanacEvent = await onCalendarSync();
            calendarSyncSuccess();
          } else {
            stateChanges.showCalendarModal = true;
          }
        }
      } catch ({ data: error }) {
        ToastService.add({
          message: `Almanac error: ${error.message}`,
          type: 'danger',
        });
      }

      setState({ ...state, ...stateChanges, almanacLoading: false });
      $location.search({});
    };
    init();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (
        showFeedback &&
        !feedback &&
        appointment.feedbackCollected &&
        ['client', 'seller', 'landlord/manager', 'tenant'].includes(contactType)
      ) {
        try {
          setFeedback(
            await getExternalFeedback({
              dashboardId,
              appointmentId: appointment.id,
            }),
          );
        } catch (err) {
          message.error('Error fetching feedback');
          errorReporter.error('Error fetching feedback', err);
        }
      }
    })();
  }, [showFeedback]);

  React.useEffect(() => {
    if (state.redirectToAppointments) {
      $location.search({});
      $location.path(`/dash/${dashboardId}/appointments`);
      $route.reload();
    }
  }, [state.redirectToAppointments]);

  if (state.sendingRequest) return null;

  return (
    <>
      <CalendarSyncProviderModal
        showModal={state.showCalendarModal}
        redirectUser={redirectCalendar}
        closeModal={() => setState({ ...state, showCalendarModal: false })}
      />
      <div className="normalheader transition animated fadeIn">
        <div className="hpanel">
          <div className="panel-body">
            <h3 className="font-light m-b-xs p-x-none col-xs-12 col-lg-9">
              {!isNewAppointment && !externalDashboard && !coopDashboard && (
                <a
                  onClick={clearSelected}
                  href={`/external/#/dash/${dashboardId}/appointments`}
                  className="btn btn-primary"
                >
                  <i className="fa fa-chevron-left"></i> Back
                </a>
              )}
              {externalDashboard ? 'Manage Showing' : 'My Showing'}
            </h3>
          </div>
        </div>
      </div>
      <div className="content animate-panel">
        {contactType !== 'seller' && isNewAppointment && (
          <NewAppointmentAlert appointment={appointment as any} />
        )}
        <div className="row">
          <div className="col-md-12">
            <div className="hpanel">
              <div className="panel-body">
                <div className="col-md-6 m-b-sm" style={{ padding: '10px 0' }}>
                  <AppointmentDetailsSection
                    appointment={appointment}
                    contactType={contactType}
                    calendarSyncButton={
                      (coopDashboard || retsDashboard) &&
                      !state.almanacLoading &&
                      appointment.status === 'confirm' && (
                        <CalendarSyncButton
                          isSyncButtonClicked={state.isSyncButtonClicked}
                          onCalendarSyncButtonClick={() =>
                            onCalendarSync().then(() => {
                              setState({
                                ...state,
                                isSyncButtonClicked: true,
                                existingAlmanacEvent: true,
                              });
                            })
                          }
                          existingAlmanacEvent={state.existingAlmanacEvent}
                          isAlmanacAuthenticated={state.isAlmanacAuthenticated}
                          startOauth={openCalendarModal}
                        />
                      )
                    }
                    external
                    $rootScope={$rootScope}
                  />
                  {externalDashboard && (
                    <div>
                      {!(
                        new Date(serverDate) >= new Date(appointment.timeEnd)
                      ) &&
                        appointment.status !== 'cancel' &&
                        appointment.status !== 'deny' && (
                          <ExternalStatusChange
                            ExternalAuth={ExternalAuth}
                            ExternalService={ExternalService}
                            ToastService={ToastService}
                            $location={$location}
                            $route={$route}
                            $rootScope={$rootScope}
                            status={newStatus}
                            appointment={appointment}
                            clearSelected={clearSelected}
                          />
                        )}
                      {(new Date(serverDate) >= new Date(appointment.timeEnd) ||
                        appointment.status === 'cancel' ||
                        appointment.status === 'deny') && (
                        <div className="appointment-change-buttons-center">
                          <a
                            onClick={clearSelected}
                            className="btn btn-default"
                            href={`/external/#${backLinkRouteForExternalDashboard}`}
                          >
                            Back
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  {coopDashboard && (
                    <>
                      {appointment.status === 'suggested_time_change' &&
                        !appointment.keepOriginalAppointment && (
                          <div
                            style={{
                              margin: '0 0 20px 10px',
                              textTransform: 'initial',
                            }}
                          >
                            If you deny this suggestion,{' '}
                            <span className="attention">
                              your original showing will also be cancelled.
                            </span>
                          </div>
                        )}
                      <div className="row appointment-change-buttons-center">
                        {!(
                          new Date(serverDate) >= new Date(appointment.timeEnd)
                        ) &&
                          !['cancel', 'deny', 'suggested_time_change'].includes(
                            appointment.status,
                          ) && (
                            <a
                              style={{
                                textTransform: 'uppercase',
                                marginRight: 10,
                              }}
                              className="btn btn-danger"
                              href={`/external/#/dash/${dashboardId}/appointments/${appointment.id}/status/cancel`}
                              data-cy="cancel-appointment-coop"
                            >
                              Cancel Showing
                            </a>
                          )}
                        {(new Date(serverDate) <
                          new Date(appointment.timeEnd) ||
                          appointment.status !== 'confirm') &&
                          appointment.status !== 'suggested_time_change' && (
                            <a
                              style={{
                                textTransform: 'uppercase',
                                marginRight: 10,
                              }}
                              className="btn btn-primary"
                              href={`/external/#/dash/${dashboardId}/appointments/${appointment.id}/reschedule`}
                              data-cy="reschedule-appointment-coop"
                            >
                              Reschedule
                            </a>
                          )}
                        {new Date(serverDate) < new Date(appointment.timeEnd) &&
                          appointment.status === 'suggested_time_change' && (
                            <>
                              <a
                                className="btn btn-success"
                                onClick={coopAcceptTimeChange}
                                style={{ marginRight: 10 }}
                              >
                                Accept
                              </a>
                              <a
                                className="btn btn-danger"
                                onClick={coopDenyTimeChange}
                                style={{ marginRight: 10 }}
                              >
                                Deny
                              </a>
                            </>
                          )}
                        {new Date(serverDate) >=
                          new Date(appointment.timeEnd) &&
                          appointment.status === 'confirm' &&
                          [
                            'Buyer/Broker',
                            'Revisit',
                            'Dual Agency',
                            'Agent Inspection',
                          ].includes(appointment.type) &&
                          !appointment.feedbackCollected && (
                            <a
                              className="btn btn-default"
                              href={`/external/#/dash/${dashboardId}/appointments/${appointment.id}/feedback`}
                              style={{ marginRight: 10 }}
                            >
                              Leave Feedback
                            </a>
                          )}
                        {appointment.feedbackCollected && (
                          <button
                            className="btn btn-success btn-outline"
                            style={{ marginRight: 10, pointerEvents: 'none' }}
                          >
                            Feedback Submitted
                          </button>
                        )}
                        <a
                          onClick={clearSelected}
                          className="btn btn-default"
                          href={`/external/#/dash/${dashboardId}/appointments`}
                        >
                          Back
                        </a>
                      </div>
                    </>
                  )}
                </div>
                {contactType === 'coop' && isCovidFlagEnabled && (
                  <CovidSignRequired
                    appointment={appointment}
                    dashboardId={dashboardId}
                    country={country}
                    retsId={retsId}
                    isInternal={
                      !(coopDashboard || externalDashboard || retsDashboard)
                    }
                    cameFromSignEmail={cameFromSignEmail}
                    updateAppointment={updateAppointment}
                  />
                )}
                {contactType !== 'coop' && isCovidFlagEnabled && (
                  <COVID19AptAction
                    appointment={appointment}
                    showLinkOnly
                    isExternal
                    linkText="View Uploaded Signed Acknowledgment"
                    dashboardId={dashboardId}
                    retsId={retsId}
                    country={country}
                  />
                )}
                <div className="col-md-6 m-b-sm">
                  {contactType === 'coop' &&
                    !(
                      appointment.status === 'confirm' && appointment.isPending
                    ) && (
                      <AppointmentInstructions
                        dynamicLockBoxCode={appointment.dynamicLockBoxCode}
                        accessType={appointment.appointmentConfig?.access}
                        instructions={
                          appointment.status === 'confirm'
                            ? appointment.appointmentConfig?.instructions
                            : (appointment.lastAction?.actionDetails as any)
                                ?.instructions
                        }
                      />
                    )}
                  {state.lockboxPhoto && (
                    <div>
                      <label>Lock Box Photo:</label>
                      <div className="p-xs">
                        <img
                          src={state.lockboxPhoto}
                          style={{ maxWidth: '100%' }}
                        />
                      </div>
                    </div>
                  )}
                  {feedback && (
                    <StyledFeedbackContainer $lg={lg}>
                      <label>Feedback Provided:</label>
                      {/* @ts-expect-error: external feedback missing templates type */}
                      <FeedbackView feedback={feedback} />
                    </StyledFeedbackContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditAppointment;

const StyledFeedbackContainer = styled.div<{ $lg: boolean }>`
  ${({ $lg }) => (!$lg ? `margin: 0 -15px;` : `margin: 10px 0 0 10px;`)}
  .feedback-view {
    width: unset;
  }
`;
